/**
 * The dashboard: a list of blocks in a three-column grid. Stored as JSON in the
 * synced setting `dashboard_layout`, so every device shows the same dashboard.
 */
import { shortId } from './database';

export const DASHBOARD_SETTING = 'dashboard_layout';

export type BlockType =
  | 'day'
  | 'progress'
  | 'tasks'
  | 'database'
  | 'recent'
  | 'calendar'
  | 'projects'
  | 'memos'
  | 'note'
  | 'summary'
  | 'plugin';
export const BLOCK_TYPES: BlockType[] = [
  'day',
  'progress',
  'tasks',
  'database',
  'recent',
  'calendar',
  'projects',
  'memos',
  'note',
  'summary',
];
/** Also allowed in a stored layout: blocks added by plugins (not in the generic "add" menu). */
const STORED_TYPES: BlockType[] = [...BLOCK_TYPES, 'plugin'];

export type TasksScope = 'today' | 'week' | 'overdue' | 'inbox';
export type BlockWidth = 1 | 2 | 3;

export interface DashboardBlock {
  id: string;
  type: BlockType;
  /** Columns of the three-column grid it spans. */
  width: BlockWidth;
  /** Own title; missing → a default per type. */
  title?: string;
  /** tasks: which tasks. */
  scope?: TasksScope;
  /** database: which database and view. */
  databaseId?: string;
  viewId?: string;
  /** recent: how many pages. */
  limit?: number;
  /** plugin: the plugin (its page id) and which of its blocks. */
  pluginId?: string;
  pluginBlock?: string;
}

export interface DashboardLayout {
  version: 1;
  blocks: DashboardBlock[];
}

const WIDTHS: BlockWidth[] = [1, 2, 3];

/** The stored layout, or null when missing or unreadable. */
export function readDashboard(raw: string | null | undefined): DashboardLayout | null {
  if (!raw) return null;
  try {
    const data = JSON.parse(raw) as Partial<DashboardLayout>;
    if (!Array.isArray(data.blocks)) return null;
    return {
      version: 1,
      blocks: data.blocks
        .filter((b) => b && STORED_TYPES.includes(b.type))
        .map((b) => ({
          ...b,
          id: b.id || shortId(),
          width: WIDTHS.includes(b.width) ? b.width : 1,
        })),
    };
  } catch {
    return null;
  }
}

/**
 * What a new workspace starts with. Fixed ids: the default is stable without
 * being stored (the first change stores it), and the same on every device.
 */
export function defaultDashboard(ids: { meetingsDbId?: string | null } = {}): DashboardLayout {
  const block = (b: Omit<DashboardBlock, 'id'>): DashboardBlock => ({
    id: `default-${b.type}`,
    ...b,
  });
  return {
    version: 1,
    blocks: [
      block({ type: 'day', width: 2 }),
      block({ type: 'progress', width: 1 }),
      block({ type: 'memos', width: 1 }),
      block({ type: 'projects', width: 2 }),
      block({ type: 'recent', width: 1, limit: 6 }),
      ...(ids.meetingsDbId
        ? [block({ type: 'database', width: 3, databaseId: ids.meetingsDbId })]
        : []),
    ],
  };
}

/**
 * The 2026-10 dashboard (ADR 0028): "Mijn dag", today's progress and Onthouden
 * come first. A stored layout gets them once; its own blocks stay, except the
 * default "today" tasks block and the quick note, which "Mijn dag" and the
 * capture field replace.
 */
export const DASHBOARD_V2 = 'dashboard_v2';

export function upgradeDashboard(layout: DashboardLayout): DashboardLayout {
  if (layout.blocks.some((b) => b.type === 'day')) return layout;
  const kept = layout.blocks.filter((b) => b.id !== 'default-tasks' && b.id !== 'default-note');
  const first: DashboardBlock[] = [
    { id: 'default-day', type: 'day', width: 2 },
    { id: 'default-progress', type: 'progress', width: 1 },
    ...(kept.some((b) => b.type === 'memos')
      ? []
      : [{ id: 'default-memos', type: 'memos' as const, width: 1 as const }]),
  ];
  return { ...layout, blocks: [...first, ...kept] };
}

export function addBlock(
  layout: DashboardLayout,
  type: BlockType,
): { layout: DashboardLayout; block: DashboardBlock } {
  const block: DashboardBlock = {
    id: shortId(),
    type,
    width:
      type === 'calendar'
        ? 3
        : type === 'database' || type === 'summary' || type === 'day' || type === 'projects'
          ? 2
          : 1,
    ...(type === 'tasks' ? { scope: 'today' as const } : {}),
    ...(type === 'recent' ? { limit: 8 } : {}),
  };
  return { block, layout: { ...layout, blocks: [...layout.blocks, block] } };
}

/** A block of a plugin (ADR 0025). */
export function addPluginBlock(
  layout: DashboardLayout,
  pluginId: string,
  pluginBlock: string,
  title: string,
): DashboardLayout {
  const block: DashboardBlock = {
    id: shortId(),
    type: 'plugin',
    width: 1,
    title,
    pluginId,
    pluginBlock,
  };
  return { ...layout, blocks: [...layout.blocks, block] };
}

export function updateBlock(layout: DashboardLayout, block: DashboardBlock): DashboardLayout {
  return { ...layout, blocks: layout.blocks.map((b) => (b.id === block.id ? block : b)) };
}

export function removeBlock(layout: DashboardLayout, id: string): DashboardLayout {
  return { ...layout, blocks: layout.blocks.filter((b) => b.id !== id) };
}

/** Move a block one place earlier (-1) or later (+1). */
/** Drag and drop: put the block at the place of another block (before or after it). */
export function moveBlockTo(
  layout: DashboardLayout,
  id: string,
  targetId: string,
  side: 'before' | 'after',
): DashboardLayout {
  if (id === targetId) return layout;
  const moving = layout.blocks.find((b) => b.id === id);
  if (!moving || !layout.blocks.some((b) => b.id === targetId)) return layout;
  const blocks = layout.blocks.filter((b) => b.id !== id);
  const at = blocks.findIndex((b) => b.id === targetId) + (side === 'after' ? 1 : 0);
  blocks.splice(at, 0, moving);
  return { ...layout, blocks };
}

export function moveBlock(layout: DashboardLayout, id: string, delta: -1 | 1): DashboardLayout {
  const blocks = [...layout.blocks];
  const i = blocks.findIndex((b) => b.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= blocks.length) return layout;
  [blocks[i], blocks[j]] = [blocks[j]!, blocks[i]!];
  return { ...layout, blocks };
}
